const STORAGE_KEY = "hub_labor_dev_timeout_countdown";
const EVENT_NAME = "hub:labor-dev-timeout-countdown";

export interface LaborDevCountdownState {
  page: string;
  startedAtMs: number;
  /** Last operator attention (local). Drives whether the next heartbeat tick flushes to the database. */
  lastActivityMs: number;
  /**
   * Wall clock of the last successful `labor.device_session.last_heartbeat_at` update (or dev-only local mirror).
   * Idle timeout is evaluated from this value so development matches server-visible liveness.
   */
  lastDbHeartbeatAtMs: number;
  idleTimeoutMs: number;
  isWorking: boolean;
  updatedAtMs: number;
}

function parseCountdownPayload(value: unknown): LaborDevCountdownState | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const record = value as Record<string, unknown>;
  if (
    typeof record.page !== "string" ||
    typeof record.startedAtMs !== "number" ||
    typeof record.lastActivityMs !== "number" ||
    typeof record.idleTimeoutMs !== "number" ||
    typeof record.isWorking !== "boolean" ||
    typeof record.updatedAtMs !== "number"
  ) {
    return null;
  }
  const lastActivityMs = record.lastActivityMs;
  const rawLastDb = record.lastDbHeartbeatAtMs;
  const lastDbHeartbeatAtMs =
    typeof rawLastDb === "number" && Number.isFinite(rawLastDb) ? rawLastDb : lastActivityMs;
  return {
    page: record.page,
    startedAtMs: record.startedAtMs,
    lastActivityMs,
    lastDbHeartbeatAtMs,
    idleTimeoutMs: record.idleTimeoutMs,
    isWorking: record.isWorking,
    updatedAtMs: record.updatedAtMs,
  };
}

export function readLaborDevCountdown(): LaborDevCountdownState | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    return parseCountdownPayload(parsed);
  } catch {
    return null;
  }
}

export function writeLaborDevCountdown(state: LaborDevCountdownState): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: state }));
  } catch {
    /* localStorage can be unavailable in private windows */
  }
}

export function clearLaborDevCountdown(page: string): void {
  if (typeof window === "undefined") return;
  const current = readLaborDevCountdown();
  if (current !== null && current.page !== page) return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
    window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: null }));
  } catch {
    /* localStorage can be unavailable in private windows */
  }
}

export function subscribeLaborDevCountdown(
  listener: (state: LaborDevCountdownState | null) => void,
): () => void {
  if (typeof window === "undefined") return () => undefined;
  const onLocalEvent = (event: Event): void => {
    if (event instanceof CustomEvent) {
      const payload = event.detail as LaborDevCountdownState | null | undefined;
      if (payload == null) {
        listener(null);
        return;
      }
      listener(parseCountdownPayload(payload));
      return;
    }
    listener(readLaborDevCountdown());
  };
  const onStorage = (event: StorageEvent): void => {
    if (event.key === STORAGE_KEY) {
      listener(readLaborDevCountdown());
    }
  };
  window.addEventListener(EVENT_NAME, onLocalEvent);
  window.addEventListener("storage", onStorage);
  return () => {
    window.removeEventListener(EVENT_NAME, onLocalEvent);
    window.removeEventListener("storage", onStorage);
  };
}

